import { useQueryClient } from '@tanstack/react-query';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import i18next from 'i18next';
import { API_PATIENTS_COMMENTS_DELETE } from 'services/dataService/resources';
import { DateFormatter } from 'infrastructure/functions';
import {
  getCcmInteractionTimeKey,
  getRpmInteractionTimeKey,
} from 'components/organisms/interactiveTime/hooks';
import { UserType } from 'infrastructure/enums';

import useApi from '../use-api';
import { generateCommentsQueryKey } from './common';

import type { InfiniteData } from '@tanstack/react-query';
import type { IPaginatedCommentsData } from './types';
import type { IDeleteComment } from 'infrastructure/interfaces';

export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  const { loading, createData } = useApi({ canAbort: false });

  const deleteComment = async (body: IDeleteComment): Promise<void> => {
    try {
      if (body.userType !== UserType.Admin) {
        throw new Error('You do not have permission to delete comments');
      }
      const queryKey = generateCommentsQueryKey(body);

      await createData(API_PATIENTS_COMMENTS_DELETE, body);

      notificationController.success({
        message: i18next.t('labels.commentDeleted'),
      });

      queryClient.setQueryData<InfiniteData<IPaginatedCommentsData>>(
        queryKey,
        (od) => {
          if (!od) return od;

          const oldData = structuredClone(od);
          const newPages = oldData.pages.map((page) => ({
            ...page,
            data: page.data.filter((comment) => comment.guid !== body.guid),
          }));

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );

      const today = new Date();
      const formattedDate = DateFormatter({ date: today });

      const rpmInteractiveTime = getRpmInteractionTimeKey(
        body.patientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(rpmInteractiveTime);

      const ccmInteractiveTime = getCcmInteractionTimeKey(
        body.patientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(ccmInteractiveTime);
    } catch (error) {
      notificationController.error({
        message: i18next.t('labels.commentDeleteError'),
      });
    }
  };

  return { loading, deleteComment };
};
